import moment from 'moment';
import { TFunction } from 'i18next';
import { HorizonResult, HorizonType } from './occupancyMath';

/** Repo idiom for seconds-from-midnight: format in UTC so nothing shifts the clock face. */
export const formatSeconds = (seconds: number, timeFormat: string): string =>
  moment.unix(seconds).tz('utc').format(timeFormat);

export type AreaUnitWords = {
  /** "שולחנות" / "tables", or "עמדות" / "stations" for a ticket-counted area. */
  plural: string;
  singular: string;
  /** The word under the free/total stat — "פנוי" or "פרויקטים". */
  statLabel: string;
};

/**
 * "2½ שעות" / "3 hours" — the session length the headline stat promises to hold free. Derived from
 * the page duration, so it has to cope with any half-hour multiple.
 */
export const formatSessionLength = (sessionMinutes: number, t: TFunction): string => {
  const hours = Math.floor(sessionMinutes / 60);
  const minutes = sessionMinutes % 60;
  if (hours === 0) {
    return t('calendar.areas_view.session_minutes', { count: minutes });
  }
  if (minutes === 0) {
    return t('calendar.areas_view.session_hours', { count: hours });
  }
  if (minutes === 30) {
    return t('calendar.areas_view.session_hours_half', { hours });
  }
  return t('calendar.areas_view.session_hours_minutes', { hours, minutes });
};

export const horizonText = ({
  result,
  unitWords,
  sessionMinutes,
  timeFormat,
  t,
}: {
  result: HorizonResult;
  unitWords: AreaUnitWords;
  sessionMinutes: number;
  timeFormat: string;
  t: TFunction;
}): string => {
  const time = (seconds: number) => formatSeconds(seconds, timeFormat);

  switch (result.type) {
    case HorizonType.CLOSED_OPENS_AT:
      return t('calendar.areas_view.closed_opens_at', { time: time(result.opensAtSeconds) });
    case HorizonType.CLOSED_ALL_DAY:
      return t('calendar.areas_view.closed_all_day');
    case HorizonType.OPENS_AT:
      return t('calendar.areas_view.opens_up_at', { time: time(result.atSeconds), n: result.n });
    case HorizonType.FULL_ALL_DAY:
      return t('calendar.areas_view.full_until_end_of_day');
    case HorizonType.FREE: {
      const until =
        result.untilSeconds === null
          ? t('calendar.areas_view.free_all_day')
          : t('calendar.areas_view.free_until', { time: time(result.untilSeconds) });
      if (result.sessionWindowN <= 0) {
        return until;
      }
      const unit = result.sessionWindowN === 1 ? unitWords.singular : unitWords.plural;
      const session = t('calendar.areas_view.session_window', {
        count: result.sessionWindowN,
        unit,
        duration: formatSessionLength(sessionMinutes, t),
      });
      return `${session} · ${until}`;
    }
    default:
      return '';
  }
};
